import { MirrorVectorVerticesCommand, MoveVectorVertexCommand } from "@/commands/vectorCommands";
import { TransactionCommand } from "@/commands/transaction";
import { getAllSceneNodes } from "@/document/model/document";
import type { SceneNode } from "@/document/model/nodes";
import { isNodeLockedForEditing } from "@/document/operations/nodeLocking";
import { nodeSelectionDragDeltaToLocal, nodeSelectionWorldDeltaToLocal } from "@/document/operations/nodeWorldTransform";
import { documentStore, selectionStore } from "@/editor/store";
import type { EditorCommandHost } from "../editorCommandHost";
import { updateNodeBounds } from "./nodeHandlers";

export function nudgeNodes(
  host: EditorCommandHost,
  nodeIds: string[],
  dx: number,
  dy: number,
): void {
  const document = documentStore.getDocument();
  const nodes = getAllSceneNodes(document).filter(node => nodeIds.includes(node.id) && !isNodeLockedForEditing(document, node.id));
  updateNodeBounds(
    host,
    nodes.map(node => {
      const localDelta = nodeSelectionDragDeltaToLocal(document, node.id, { x: dx, y: dy });
      return { nodeId: node.id, bounds: { ...node.bounds, x: node.bounds.x + localDelta.x, y: node.bounds.y + localDelta.y } };
    }),
    nodes.length > 1 ? `Nudged ${nodes.length} nodes` : `Nudged ${nodes[0]?.name ?? "node"}`,
  );
}

export function nudgeSelection(host: EditorCommandHost, dx: number, dy: number): void {
  if (nudgeSelectedVectorGeometry(host, dx, dy)) {
    return;
  }

  const selection = selectionStore.getSelection();
  nudgeNodes(host, selection.selectedNodeIds, dx, dy);
}

export function nudgeSelectedVectorGeometry(host: EditorCommandHost, dx: number, dy: number): boolean {
  const selection = selectionStore.getSelection();
  if (selection.mode !== "vectorEdit" || !selection.primarySelectedNodeId) {
    return false;
  }

  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === selection.primarySelectedNodeId);
  if (node?.type !== "vectorNetwork") {
    return false;
  }

  const selectedEdgeIds = selection.selectedEdgeIds ?? [];
  if (selectedEdgeIds.length > 0) {
    const localDelta = nodeSelectionWorldDeltaToLocal(document, selection.primarySelectedNodeId, { x: dx, y: dy });
    const positions = vectorEdgeEndpointPositions(node, selectedEdgeIds, localDelta.x, localDelta.y);
    return nudgeVectorVertexPositions(host, selection.primarySelectedNodeId, positions, {
      selectedVertexId: undefined,
      selectedVertexIds: [],
      selectedEdgeIds,
      label: selectedEdgeIds.length > 1 ? "Nudged edges" : "Nudged edge",
    });
  }

  const selectedVertexIds = selection.selectedVertexIds ?? [];
  const vertexIds = selectedVertexIds.length > 0 ? selectedVertexIds : selection.selectedVertexId ? [selection.selectedVertexId] : [];
  if (vertexIds.length === 0) {
    return false;
  }

  const localDelta = nodeSelectionWorldDeltaToLocal(document, selection.primarySelectedNodeId, { x: dx, y: dy });
  const selectedIds = new Set(vertexIds);
  const positions = node.network.vertices
    .filter(vertex => selectedIds.has(vertex.id))
    .map(vertex => ({ vertexId: vertex.id, x: vertex.x + localDelta.x, y: vertex.y + localDelta.y }));
  return nudgeVectorVertexPositions(host, selection.primarySelectedNodeId, positions, {
    selectedVertexId: vertexIds.at(-1),
    selectedVertexIds: vertexIds,
    selectedEdgeIds: [],
    label: vertexIds.length > 1 ? "Nudged vertices" : "Nudged vertex",
  });
}

/**
 * Mirrors the selected vertices (and their handles) in vector edit mode about
 * the horizontal/vertical symmetry line through their combined bounding box
 * center. Returns false when there is no vector sub-selection so callers can
 * fall back to flipping the whole node.
 */
export function mirrorSelectedVectorGeometry(host: EditorCommandHost, axis: "horizontal" | "vertical"): boolean {
  const selection = selectionStore.getSelection();
  if (selection.mode !== "vectorEdit" || !selection.primarySelectedNodeId) {
    return false;
  }

  const document = documentStore.getDocument();
  const node = getAllSceneNodes(document).find(candidate => candidate.id === selection.primarySelectedNodeId);
  if (node?.type !== "vectorNetwork") {
    return false;
  }

  const vertexIds = selectedVectorEditVertexIds(node, selection.selectedEdgeIds, selection.selectedVertexIds, selection.selectedVertexId);
  if (vertexIds.length === 0) {
    return false;
  }

  const selected = new Set(vertexIds);
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const vertex of node.network.vertices) {
    if (!selected.has(vertex.id)) {
      continue;
    }
    minX = Math.min(minX, vertex.x);
    minY = Math.min(minY, vertex.y);
    maxX = Math.max(maxX, vertex.x);
    maxY = Math.max(maxY, vertex.y);
  }
  const center = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };

  const label = axis === "horizontal" ? "Mirrored vertices horizontally" : "Mirrored vertices vertically";
  const nextSelection = host.selectionSnapshot({
    selectedVertexId: selection.selectedVertexId,
    selectedVertexIds: selection.selectedVertexIds ?? [],
    selectedEdgeIds: selection.selectedEdgeIds ?? [],
    selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(
    new MirrorVectorVerticesCommand(node.id, vertexIds, axis, center),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, label);
  return true;
}

function selectedVectorEditVertexIds(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  selectedEdgeIds: string[] | undefined,
  selectedVertexIds: string[] | undefined,
  selectedVertexId: string | undefined,
): string[] {
  const edgeIds = selectedEdgeIds ?? [];
  if (edgeIds.length > 0) {
    const selectedEdges = new Set(edgeIds);
    const vertexIds: string[] = [];
    for (const edge of node.network.edges) {
      if (!selectedEdges.has(edge.id)) {
        continue;
      }
      for (const vertexId of [edge.from.vertexId, edge.to.vertexId]) {
        if (!vertexIds.includes(vertexId)) {
          vertexIds.push(vertexId);
        }
      }
    }
    return vertexIds;
  }

  const vertexIds = selectedVertexIds ?? [];
  if (vertexIds.length > 0) {
    return vertexIds;
  }
  return selectedVertexId ? [selectedVertexId] : [];
}

export function nudgeVectorVertexPositions(
  host: EditorCommandHost,
  nodeId: string,
  positions: Array<{ vertexId: string; x: number; y: number }>,
  selection: { selectedVertexId?: string; selectedVertexIds: string[]; selectedEdgeIds: string[]; label: string },
): boolean {
  const node = getAllSceneNodes(documentStore.getDocument()).find(candidate => candidate.id === nodeId);
  if (node?.type !== "vectorNetwork" || positions.length === 0) {
    return false;
  }

  const commands = positions.flatMap(position => {
    const vertex = node.network.vertices.find(candidate => candidate.id === position.vertexId);
    return vertex && (vertex.x !== position.x || vertex.y !== position.y)
      ? [new MoveVectorVertexCommand(nodeId, position.vertexId, { x: vertex.x, y: vertex.y }, { x: position.x, y: position.y })]
      : [];
  });
  if (commands.length === 0) {
    return true;
  }

  selectionStore.updateFreshCutVertexIds(current => current.filter(id => !positions.some(position => position.vertexId === id)));
  const nextSelection = host.selectionSnapshot({
    selectedVertexId: selection.selectedVertexId,
    selectedVertexIds: selection.selectedVertexIds,
    selectedEdgeIds: selection.selectedEdgeIds,
    selectedHandle: undefined,
  });
  const context = host.commandContext();
  host.history.execute(
    commands.length === 1 ? commands[0]! : new TransactionCommand(selection.label, commands),
    context,
    { afterSelection: nextSelection },
  );
  host.applySelectionSnapshot(nextSelection);
  host.commit(context.document, selection.label);
  return true;
}

function vectorEdgeEndpointPositions(
  node: Extract<SceneNode, { type: "vectorNetwork" }>,
  edgeIds: string[],
  dx: number,
  dy: number,
): Array<{ vertexId: string; x: number; y: number }> {
  const selectedEdges = new Set(edgeIds);
  const vertexIds: string[] = [];
  for (const edge of node.network.edges) {
    if (!selectedEdges.has(edge.id)) {
      continue;
    }
    for (const vertexId of [edge.from.vertexId, edge.to.vertexId]) {
      if (!vertexIds.includes(vertexId)) {
        vertexIds.push(vertexId);
      }
    }
  }

  return vertexIds.flatMap(vertexId => {
    const vertex = node.network.vertices.find(candidate => candidate.id === vertexId);
    return vertex ? [{ vertexId, x: vertex.x + dx, y: vertex.y + dy }] : [];
  });
}
